import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { createPortal } from "react-dom"
import { useOV25UI } from "../../contexts/ov25-ui-context.js"
import { Ov25ShadowHost } from "../Ov25ShadowHost.js"

import { cn } from "../../utils/cn.js"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  // z-index 2147483646 is max-2
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "ov:fixed ov:inset-0 ov:z-2147483646 ov:bg-black/80  data-[state=open]:ov:animate-in data-[state=closed]:ov:animate-out data-[state=closed]:ov:fade-out-0 data-[state=open]:ov:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { closeClassName?: string }
>(({ className, children, id, closeClassName, ...props }, ref) => {
  const { shadowDOMs } = useOV25UI();
  
  // Snap2 dialogs need special handling for fullscreen compatibility
  const isSnap2Dialog = className?.includes('snap2');
  // SwatchBook dialogs should use the swatchbook portal
  const isSwatchBookDialog = id === 'ov25-swatchbook';
  // AR preview dialog should always use document.body to ensure visibility in fullscreen
  const isArPreviewDialog = id === 'ov25-ar-preview-qr-code-dialog';
  // Always use document.body for snap2 dialogs and AR preview dialogs to ensure top-most stacking above modals
  const portalTarget = isSnap2Dialog || isArPreviewDialog
    ? document.body
    : isSwatchBookDialog
    ? (shadowDOMs?.swatchbookPortal || document.body)
    : (shadowDOMs?.configuratorViewControls || shadowDOMs?.swatchbookPortal || document.body);
  
  if (!portalTarget) {
    throw new Error('Portal target not found');
  }

  const contentClassName = cn(
    "ov:fixed ov:left-[50%] ov:top-[50%] ov:z-2147483646 ov:grid ov:w-full ov:max-w-lg ov:translate-x-[-50%] ov:translate-y-[-50%] ov:gap-4 ov:border ov:bg-background ov:p-6 ov:shadow-lg ov:duration-200 data-[state=open]:ov:animate-in data-[state=closed]:ov:animate-out data-[state=closed]:ov:fade-out-0 data-[state=open]:ov:fade-in-0 data-[state=closed]:ov:zoom-out-95 data-[state=open]:ov:zoom-in-95 data-[state=closed]:ov:slide-out-to-left-1/2 data-[state=closed]:ov:slide-out-to-top-[48%] data-[state=open]:ov:slide-in-from-left-1/2 data-[state=open]:ov:slide-in-from-top-[48%] ov:rounded-lg",
    className
  );

  const content = (
    <DialogPrimitive.Content ref={ref} id={id} className={contentClassName} {...props}>
      {children}
      <DialogPrimitive.Close className={cn("ov:absolute ov:right-4 ov:top-4 ov:rounded-sm ov:opacity-70 ov:cursor-pointer ov:ring-offset-background ov:transition-opacity hover:ov:opacity-100 focus:ov:outline-none focus:ov:ring-2 focus:ov:ring-ring focus:ov:ring-offset-2 disabled:ov:pointer-events-none data-[state=open]:ov:bg-accent data-[state=open]:ov:text-muted-foreground", closeClassName)}>
        <X className="ov:h-6 ov:w-6" />
        <span className="ov:sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  );

  if (isSnap2Dialog || isArPreviewDialog) {
    return createPortal(
      <Ov25ShadowHost
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2147483647,
          // Host must not capture hits: Radix uses pointer-events: none on overlay/content when
          // closed; an auto host would block the whole page. Shadow descendants still receive events.
          pointerEvents: 'none',
        }}
      >
        <DialogOverlay />
        {content}
      </Ov25ShadowHost>,
      portalTarget
    );
  }

  return (
    <>
      {createPortal(<DialogOverlay />, portalTarget)}
      {createPortal(content, portalTarget)}
    </>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "ov:flex ov:flex-col ov:space-y-1.5 ov:text-center ov:sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "ov:flex ov:flex-col-reverse ov:sm:flex-row ov:sm:justify-end ov:sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "ov:text-lg ov:font-semibold ov:leading-none ov:tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("ov:text-sm ov:text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
